import React, { BaseSyntheticEvent, useRef, useState } from 'react'
import { LanguageToggleProps } from '../icons/LanguageToggle'
import { useAppDispatch, useAppSelector } from '../../utils/storeHooks';
import { inputEmail_edits, inputName_edits, reset_edits } from '../../utils/editsSlice';
import { useWhakamohioMaiMutation } from '../../utils/apiSlice';
import SubmitButton from '../form_components/SubmitButton';
import XLoader from '../icons/XLoader';
import Outcome from '../toast/Outcome';
import { vf } from '../../utils/customTypes';
import { EmailRegex } from '../../utils/emailRegex';

type StayInformedProps = LanguageToggleProps & {
    toggleForm: vf
}

const StayInformedForm = ({reo, toggleForm}: StayInformedProps) => {

    const dispatch = useAppDispatch();
    const { name, email } = useAppSelector(store => store.edits);
    const [showOutcome, setShowOutcome] = useState('');
    const [ nameIsMissing, setNameIsMissing ] = useState(false);
    const [ emailIsMissing, setEmailIsMissing ] = useState(false);
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const isDisabled = useRef(true);
    const [ submitEmail ] = useWhakamohioMaiMutation();
    const emailRegex = new EmailRegex();

    let nameLabel, namePlaceholder, emailLabel, emailPlaceholder, button, title;

    if (reo) {
        title = 'Whiwhi pānui';
        nameLabel = 'Tō ingoa:';
        namePlaceholder = 'Taku Ingoa';
        emailLabel = 'Tō īmēra:'
        emailPlaceholder = 'takuimera@tetehiwahi.nz';
        button = 'pānui mai';
    } else {
        title = 'Get the pānui';
        nameLabel = 'Name:';
        namePlaceholder = 'My Name';
        emailLabel = 'Email address:'
        emailPlaceholder = 'myemail@provider.nz';
        button = 'notify me';
    }

    const handleNameInput = (e: BaseSyntheticEvent) => {
        dispatch(inputName_edits(e.target.value))
    }

    const handleEmailInput = (e: BaseSyntheticEvent) => {
        dispatch(inputEmail_edits(e.target.value))
    }

    const handleSubmission = async (e: BaseSyntheticEvent) => {

        e.preventDefault();

        const triggerFormError = (n: number) => { // handles which errors should be shown
            
            const missingName = () => {
                setNameIsMissing(true);
                setTimeout(() => { setNameIsMissing(false) }, 3000);
            };

            const missingEmail = () => {
                setEmailIsMissing(true);
                setTimeout(() => { setEmailIsMissing(false) }, 3000);
            }

            switch (n) {
                case (1): {
                    console.log('missing name');
                    missingName();
                    break;
                };
                case (2): {
                    console.log('missing email');
                    missingEmail();
                    break;
                }
                case (3): {
                    console.log('empty form');
                    missingName();
                    missingEmail();
                    break;
                }
            }
        }

        let formErrors = 0;

        if (name.trim().length === 0) {
            formErrors += 1;
        };

        if (!emailRegex.test(email)) {
            formErrors += 2;
        };

        if (formErrors > 0) { // Prevent submission if necessary info is not there;
            return triggerFormError(formErrors);
        }

        setIsSubmitting(true);
        
        try {
            const submission = await submitEmail({ name, email }).unwrap();

            if (submission && submission.success) { //if submission was successful...
                dispatch(reset_edits('email'));
                toggleForm(); //set feedback on successful submission;
            } else if (submission.status === 429) {
                setShowOutcome('āmikiRawa'); //set feedback on unsuccessful submission;
            } else {
                throw new Error();
            }
        } catch {
            setShowOutcome('auē'); //set feedback on unsuccessful submission;
        }

        setIsSubmitting(false);
        setTimeout( () => {setShowOutcome('') }, 6000); // Enables further notifications to be visible
    }

    const fieldStyle = 'relative md:flex w-full';
    const labelStyle = 'md:absolute md:-left-32 max-md:pb-1 md:w-30 md:text-right md:mr-2 md:pt-2';
    const inputStyle = "grow max-md:w-full px-3 py-2 bg-kaho-0 placeholder:text-toto-0 max-sm:py-3 ";
    const errorStyling = 'outline-3 outline-toto-2 outline-offset-1 ';
    const nameInput = nameIsMissing ? inputStyle + errorStyling : inputStyle;
    const emailInput = emailIsMissing ? inputStyle + errorStyling : inputStyle;

    if (name.trim().length > 0 && emailRegex.test(email)) {
        isDisabled.current = false;
    } else {
        isDisabled.current = true;
    }

    return (
        <>
            <form onSubmit={handleSubmission} className="flex flex-col max-sm:w-[calc(100%-40px)] w-100 gap-2 my-6">
                <h2 className='font-light'>{title}</h2>
                <div className={fieldStyle}>
                    <label htmlFor='pIngoa' className={labelStyle}>{nameLabel}</label>
                    <input id='pIngoa' className={nameInput} type="input" placeholder={namePlaceholder} value={name} onChange={handleNameInput} />
                </div>
                <div className={fieldStyle}>
                    <label htmlFor='pImera' className={labelStyle}>{emailLabel}</label>
                    <input id='pImera' className={emailInput} type="email" placeholder={emailPlaceholder} value={email} onChange={handleEmailInput} />
                </div>
                <div className='w-full flex flex-row-reverse max-md:mt-2'>
                    { 
                        isSubmitting ? 
                            <div className='w-8.5'><XLoader color1="toto-2" /></div> 
                        : 
                            <SubmitButton text={button} imDisabled={isDisabled.current} /> 
                    }
                </div>
            </form>
            { 
                showOutcome && <Outcome outcome={showOutcome} reo={reo} /> 
            }
        </>
    )
}

export default StayInformedForm
